import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { Icon } from '../icons/Icon.tsx'

export type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  'type'
> & {
  type?: string
} & VariantProps<typeof checkboxVariants>

const checkboxVariants = cva(
  `
    peer h-4 w-4 shrink-0 rounded-sm border ring-offset-background

    disabled:cursor-not-allowed disabled:opacity-50

    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    focus-visible:ring-ring
  `,
  {
    variants: {
      color: {
        primary: `
          border-primary

          data-[state=checked]:bg-primary data-[state=checked]:text-primary
        `,
        secondary: `
          border-secondary

          data-[state=checked]:bg-secondary data-[state=checked]:text-secondary
        `,
        sexualDollsMansion: `
          border-[#C6C2CF] border-primary-600

          data-[state=checked]:border-primary-600
          data-[state=checked]:bg-primary-600
          data-[state=checked]:text-primary-600
        `,
        gamaNeutral: `
          border-neutral-500 bg-white

          data-[state=checked]:bg-primary data-[state=checked]:text-neutral-500
        `,
      },
    },
    defaultVariants: {
      color: 'primary',
    },
  },
)

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> &
    VariantProps<typeof checkboxVariants> & {
      iconClassName?: string
      onCheckedChange?: (checked: boolean) => void
    }
>(
  (
    { className, color = 'primary', iconClassName, onCheckedChange, ...props },
    ref,
  ) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(checkboxVariants({ color, className }))}
      {...props}
      onCheckedChange={onCheckedChange}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex items-center justify-center text-white')}
      >
        <Icon name="basic/check" size="10px" className={iconClassName} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
